import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  CloudQueue as CloudIcon,
  WhatsApp as WhatsAppIcon,
  Mail as MailIcon,
  Psychology as AIIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const StatusPage: React.FC = () => {
  // Simulação de dados do status do sistema
  const systemStatus = {
    version: '1.0.0',
    overall: 'online',
    uptime: '99.9%',
    lastUpdate: new Date().toLocaleString(),
    services: [
      { name: 'WhatsApp Bot', status: 'online', latency: '45ms' },
      { name: 'E-mail Service', status: 'online', latency: '120ms' },
      { name: 'AI Service', status: 'online', latency: '200ms' },
      { name: 'Database', status: 'online', latency: '35ms' },
    ],
    resources: {
      cpu: 45,
      memory: 60,
      storage: 30,
    },
  };

  const getStatusIcon = (status: string) => {
    return status === 'online' ? (
      <CheckCircleIcon sx={{ color: '#4caf50' }} />
    ) : (
      <WarningIcon sx={{ color: '#ff9800' }} />
    );
  };

  const getServiceIcon = (serviceName: string) => {
    switch (serviceName) {
      case 'WhatsApp Bot':
        return <WhatsAppIcon sx={{ color: '#25D366' }} />;
      case 'E-mail Service':
        return <MailIcon sx={{ color: '#1976d2' }} />;
      case 'AI Service':
        return <AIIcon sx={{ color: '#9c27b0' }} />;
      case 'Database':
        return <StorageIcon sx={{ color: '#ff9800' }} />;
      default:
        return <CheckCircleIcon />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Status do Sistema
        </Typography>

        <Grid container spacing={3}>
          {/* Status Geral */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getStatusIcon(systemStatus.overall)}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    Status Geral: {systemStatus.overall === 'online' ? 'Online' : 'Com Problemas'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <InfoIcon sx={{ color: '#1976d2', mr: 1 }} />
                  <Typography color="textSecondary">
                    Versão: {systemStatus.version}
                  </Typography>
                </Box>
                <Typography color="textSecondary">
                  Última atualização: {systemStatus.lastUpdate}
                </Typography>
                <Typography color="textSecondary">
                  Uptime: {systemStatus.uptime}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Recursos do Sistema */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Recursos do Sistema
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SpeedIcon sx={{ mr: 1, color: '#1976d2' }} />
                    <Typography>CPU</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={systemStatus.resources.cpu} 
                    sx={{ mb: 1, height: 8, borderRadius: 5 }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {systemStatus.resources.cpu}% em uso
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MemoryIcon sx={{ mr: 1, color: '#9c27b0' }} />
                    <Typography>Memória</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={systemStatus.resources.memory} 
                    sx={{ mb: 1, height: 8, borderRadius: 5 }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {systemStatus.resources.memory}% em uso
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <StorageIcon sx={{ mr: 1, color: '#ff9800' }} />
                    <Typography>Armazenamento</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={systemStatus.resources.storage} 
                    sx={{ mb: 1, height: 8, borderRadius: 5 }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {systemStatus.resources.storage}% em uso
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Status dos Serviços */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status dos Serviços
                </Typography>
                <List>
                  {systemStatus.services.map((service, index) => (
                    <React.Fragment key={service.name}>
                      <ListItem>
                        <ListItemIcon>
                          {getServiceIcon(service.name)}
                        </ListItemIcon>
                        <ListItemText 
                          primary={service.name}
                          secondary={`Latência: ${service.latency}`}
                        />
                        <Chip
                          icon={getStatusIcon(service.status)}
                          label={service.status.toUpperCase()}
                          color={service.status === 'online' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </ListItem>
                      {index < systemStatus.services.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default StatusPage; 